import { toast } from "react-toastify"
import {
    AccountOption, CodeDescription, CostCodePayload, ExpenseBudget, ExpenseBudgetAlert, ExpenseBudgetAlertStatus,
    ExpenseBudgetCostCode, ExpenseBudgetCurrency, ExpenseBudgetLine, ExpenseBudgetLinePayload,
    ExpenseBudgetPayload, ExpenseBudgetSearchFields, IExpenseBudgetQueryParams,
    ExpenseBudgetVendor, LineMappingPayload, PlanVsActual, PlanView, VendorPayload,
    BillLinkPayload, VendorBill, VendorCodeOption,
} from "models/expense-budget"

// API calls only: no Redux dispatches here (see CLAUDE.md, thunk/Redux separation).

const BASE_URL = `${process.env.REACT_APP_API_URL}/expense-budget`

interface RequestOptions {
    method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE"
    body?: unknown
    errorMessage: string
    successMessage?: string
}

// Returns the response's `data` (or true when there is none), or null after showing the error.
const request = async <T>(path: string, options: RequestOptions): Promise<T | null> => {
    try {
        const res = await fetch(`${BASE_URL}${path}`, {
            method: options.method ?? "GET",
            credentials: "include",
            headers: options.body === undefined ? undefined : { "Content-Type": "application/json" },
            body: options.body === undefined ? undefined : JSON.stringify(options.body),
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.message)

        if (options.successMessage) toast.success(options.successMessage)
        return (json.data ?? true) as T
    } catch (err: any) {
        toast.error(err.message ?? options.errorMessage)
        return null
    }
}

const toQueryString = (query: IExpenseBudgetQueryParams): string => {
    const params = new URLSearchParams()
    params.set("page", String(query.page))
    params.set("limit", String(query.limit))
    if (query.fiscalYearId) params.set("fiscalYearId", String(query.fiscalYearId))
    query.departmentIds?.forEach(id => params.append("departmentIds", id))
    return params.toString()
}

// ---------- Budgets ----------

export const getExpenseBudgetSearchFields = () =>
    request<ExpenseBudgetSearchFields>("/search-fields", { errorMessage: "Error loading budget options" })

export const getExpenseBudgets = (query: IExpenseBudgetQueryParams) =>
    request<{ data: ExpenseBudget[]; count: number; page: number; limit: number }>(
        `?${toQueryString(query)}`, { errorMessage: "Error loading expense budgets" })

export const getExpenseBudget = (id: string) =>
    request<ExpenseBudget>(`/${id}`, { errorMessage: "Error loading expense budget" })

export const createExpenseBudget = (payload: ExpenseBudgetPayload) =>
    request<ExpenseBudget>("", { method: "POST", body: payload, errorMessage: "Error creating budget", successMessage: "Budget created." })

export const updateExpenseBudget = (id: string, payload: ExpenseBudgetPayload) =>
    request<ExpenseBudget>(`/${id}`, { method: "PATCH", body: payload, errorMessage: "Error saving budget", successMessage: "Budget saved." })

export const saveExpenseBudgetExchangeRates = (id: string, rates: { currency: ExpenseBudgetCurrency; rateToCad: number }[]) =>
    request<true>(`/${id}/exchange-rates`, { method: "PUT", body: rates, errorMessage: "Error saving exchange rates", successMessage: "Exchange rates saved." })

export const saveExpenseBudgetRecipients = (id: string, employeeIds: string[]) =>
    request<true>(`/${id}/recipients`, { method: "PUT", body: { employeeIds }, errorMessage: "Error saving recipients", successMessage: "Recipients saved." })

// ---------- Cost codes ----------

// Projects with GL spending in the last 12 months.
export const getExpenseBudgetProjectOptions = (id: string) =>
    request<CodeDescription[]>(`/${id}/projects`, { errorMessage: "Error loading projects" })

// GL accounts the budget's department posted on the project this fiscal year, with totals.
export const getExpenseBudgetAccountOptions = (id: string, projectId: string) =>
    request<AccountOption[]>(`/${id}/accounts?projectId=${projectId}`, { errorMessage: "Error loading GL accounts" })

export const createExpenseBudgetCostCode = (id: string, payload: CostCodePayload) =>
    request<ExpenseBudgetCostCode>(`/${id}/cost-code`, { method: "POST", body: payload, errorMessage: "Error adding cost code", successMessage: "Cost code added." })

export const updateExpenseBudgetCostCode = (id: string, costCodeId: string, payload: CostCodePayload) =>
    request<ExpenseBudgetCostCode>(`/${id}/cost-code/${costCodeId}`, { method: "PATCH", body: payload, errorMessage: "Error saving cost code", successMessage: "Cost code saved." })

export const deleteExpenseBudgetCostCode = (id: string, costCodeId: string) =>
    request<true>(`/${id}/cost-code/${costCodeId}`, { method: "DELETE", errorMessage: "Error removing cost code", successMessage: "Cost code removed." })

// ---------- Lines, mapping and plan vs actual ----------

export const getExpenseBudgetLines = (id: string) =>
    request<ExpenseBudgetLine[]>(`/${id}/line`, { errorMessage: "Error loading budget lines" })

export const createExpenseBudgetVendor = (id: string, payload: VendorPayload) =>
    request<ExpenseBudgetVendor>(`/${id}/vendor`, { method: "POST", body: payload, errorMessage: "Error adding vendor", successMessage: "Vendor added." })

export const updateExpenseBudgetVendor = (id: string, vendorId: string, payload: VendorPayload) =>
    request<ExpenseBudgetVendor>(`/${id}/vendor/${vendorId}`, { method: "PATCH", body: payload, errorMessage: "Error saving vendor", successMessage: "Vendor saved." })

export const deleteExpenseBudgetVendor = (id: string, vendorId: string) =>
    request<true>(`/${id}/vendor/${vendorId}`, { method: "DELETE", errorMessage: "Error deleting vendor", successMessage: "Vendor deleted." })

export const createExpenseBudgetLine = (id: string, payload: ExpenseBudgetLinePayload) =>
    request<ExpenseBudgetLine>(`/${id}/line`, { method: "POST", body: payload, errorMessage: "Error adding product", successMessage: "Product added." })

export const updateExpenseBudgetLine = (id: string, lineId: string, payload: ExpenseBudgetLinePayload) =>
    request<ExpenseBudgetLine>(`/${id}/line/${lineId}`, { method: "PATCH", body: payload, errorMessage: "Error saving product", successMessage: "Product saved." })

export const deleteExpenseBudgetLine = (id: string, lineId: string) =>
    request<true>(`/${id}/line/${lineId}`, { method: "DELETE", errorMessage: "Error deleting product", successMessage: "Product deleted." })

export const saveExpenseBudgetLineMapping = (id: string, mappings: LineMappingPayload[]) =>
    request<true>(`/${id}/line-mapping`, { method: "PATCH", body: mappings, errorMessage: "Error saving mapping", successMessage: "Mapping saved." })

// ---------- AP bills ----------

export const getExpenseBudgetVendorCodes = () =>
    request<VendorCodeOption[]>(`/vendor-codes`, { errorMessage: "Error loading Acumatica vendors" })

export const getExpenseBudgetVendorBills = (id: string, vendorId: string) =>
    request<VendorBill[]>(`/${id}/bills?vendorId=${vendorId}`, { errorMessage: "Error loading bills" })

export const saveExpenseBudgetBillLinks = (id: string, links: BillLinkPayload[]) =>
    request<true>(`/${id}/bill-links`, { method: "PUT", body: links, errorMessage: "Error saving bill links", successMessage: "Bill links saved." })

export const getExpenseBudgetPlanVsActual = (id: string, view: PlanView) =>
    request<PlanVsActual>(`/${id}/plan-vs-actual?view=${view}`, { errorMessage: "Error loading plan vs actual" })

// ---------- Alerts ----------

export const getExpenseBudgetAlerts = (id: string) =>
    request<ExpenseBudgetAlert[]>(`/${id}/alerts`, { errorMessage: "Error loading alerts" })

export const updateExpenseBudgetAlert = (alertId: string, status: ExpenseBudgetAlertStatus, comment?: string) =>
    request<ExpenseBudgetAlert>(`/alert/${alertId}`, { method: "PATCH", body: { status, comment }, errorMessage: "Error updating alert", successMessage: "Alert updated." })
